import { type CSSProperties, memo, type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import { CheckIcon, CopyIcon } from "../icons";
import { cn, focusRing } from "../utils";
import type { Token } from "./highlight";
import { grammarFor } from "./languages";

// A code block in a message: the language, a Copy button, and the code with its spacing kept.
// Highlighting arrives once the block scrolls into view.

/** Code longer than this shows without colours, which keeps very long pastes quick. */
const MAX_HIGHLIGHT = 20_000;

/** Colours for highlighted code, from the Rhema palette. Ink stays for plain text. */
const SCOPE_COLOURS: Record<string, CSSProperties> = {
  keyword: { color: "#24418a" },
  built_in: { color: "#24418a" },
  "selector-tag": { color: "#24418a" },
  doctag: { color: "#24418a" },
  literal: { color: "#8a5a00" },
  number: { color: "#8a5a00" },
  symbol: { color: "#8a5a00" },
  bullet: { color: "#8a5a00" },
  attr: { color: "#8a5a00" },
  string: { color: "#3f6b5e" },
  regexp: { color: "#3f6b5e" },
  addition: { color: "#3f6b5e" },
  "template-tag": { color: "#3f6b5e" },
  comment: { color: "#6b6f7c", fontStyle: "italic" },
  quote: { color: "#6b6f7c", fontStyle: "italic" },
  title: { color: "#8f3550" },
  section: { color: "#8f3550" },
  name: { color: "#8f3550" },
  tag: { color: "#8f3550" },
  type: { color: "#2f5a86" },
  attribute: { color: "#2f5a86" },
  property: { color: "#2f5a86" },
  params: { color: "#3b3f4b" },
  variable: { color: "#6b4b0e" },
  "template-variable": { color: "#6b4b0e" },
  meta: { color: "#6b5a3e" },
  deletion: { color: "#b3261e" },
  emphasis: { fontStyle: "italic" },
  strong: { fontWeight: 600 },
};

function styleOf(scope: string | null): CSSProperties | undefined {
  if (!scope) return undefined;
  // "title.function_" takes the colour of "title".
  return SCOPE_COLOURS[scope] ?? SCOPE_COLOURS[scope.split(".")[0] ?? ""];
}

type Highlighter = (code: string, lang: string | null) => Promise<Token[] | null>;
let highlighter: Promise<Highlighter> | null = null;
function loadHighlighter(): Promise<Highlighter> {
  highlighter ??= import("./highlight").then((m) => m.highlight);
  highlighter.catch(() => {
    highlighter = null;
  });
  return highlighter;
}

/** Highlighted code already worked out, so a block scrolled back into view colours at once. */
const done = new Map<string, Token[] | null>();
const DONE_LIMIT = 300;

function remember(key: string, tokens: Token[] | null) {
  done.set(key, tokens);
  if (done.size > DONE_LIMIT) {
    const oldest = done.keys().next();
    if (!oldest.done) done.delete(oldest.value);
  }
}

/**
 * Runs `onVisible` once the element is on screen, or straight away where that cannot be told.
 * Watching starts again when `watch` changes, and stops when it is null.
 */
function useOnScreen(ref: RefObject<Element | null>, watch: string | null, onVisible: () => void) {
  const callback = useRef(onVisible);
  callback.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (watch === null || !el) return;
    if (typeof IntersectionObserver === "undefined") {
      callback.current();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          callback.current();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, watch]);
}

export type CodeBlockProps = {
  code: string;
  lang: string | null;
  /** Writes text to the clipboard. Defaults to the browser clipboard. */
  copy?: (text: string) => Promise<void> | void;
};

function CopyButton({ code, copy }: { code: string; copy?: CodeBlockProps["copy"] }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const onClick = async () => {
    try {
      await (copy ? copy(code) : navigator.clipboard.writeText(code));
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={copied ? "Copied" : "Copy code"}
      className={cn(
        "flex h-7 cursor-pointer items-center gap-1.5 rounded-[7px] border border-[#d9d6cc] bg-surface px-2.5 text-xs font-semibold text-blue select-none",
        focusRing,
      )}
    >
      {copied ? <CheckIcon size={13} strokeWidth={2.4} /> : <CopyIcon size={13} strokeWidth={2} />}
      <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
    </button>
  );
}

export const CodeBlock = /* @__PURE__ */ memo(function CodeBlock({ code, lang, copy }: CodeBlockProps) {
  const key = `${lang ?? ""}\u0000${code}`;
  const [tokens, setTokens] = useState<Token[] | null>(() => done.get(key) ?? null);
  const pre = useRef<HTMLPreElement>(null);
  const keyRef = useRef(key);
  keyRef.current = key;
  const wanted = grammarFor(lang) !== null && code.length <= MAX_HIGHLIGHT && !done.has(key);

  useOnScreen(pre, wanted ? key : null, () => {
    loadHighlighter()
      .then((highlight) => highlight(code, lang))
      .then((result) => {
        remember(key, result);
        // Only if this block still shows the same code.
        setTokens((current) => (keyRef.current === key ? result : current));
      })
      .catch(() => {
        // The code stays readable without colours.
      });
  });

  // A different block reusing this component starts plain until its own colours arrive.
  useEffect(() => {
    setTokens(done.get(key) ?? null);
  }, [key]);

  let body: ReactNode = code;
  if (tokens) {
    body = tokens.map((t, i) => {
      const style = styleOf(t.scope);
      return style ? (
        // biome-ignore lint/suspicious/noArrayIndexKey: tokens keep their order and never move
        <span key={i} style={style}>
          {t.text}
        </span>
      ) : (
        t.text
      );
    });
  }

  return (
    <div
      className="my-0.5 overflow-hidden rounded-[10px] border border-hairline bg-[#f6f5f1]"
      data-code-block=""
    >
      <div className="flex items-center justify-between gap-3 border-b border-hairline py-1.5 pr-2.5 pl-3.5">
        <span className="truncate text-xs text-ink-secondary select-none">{lang || "Code"}</span>
        <CopyButton code={code} copy={copy} />
      </div>
      <pre
        ref={pre}
        className="m-0 overflow-x-auto px-3.5 py-3 font-mono text-[13px] leading-[1.6] whitespace-pre text-ink select-text"
        style={{ tabSize: 4 }}
      >
        <code>{body}</code>
      </pre>
    </div>
  );
});
